// Doctrine gating — classifies why an invest button is disabled so the UI
// can show a useful hint ("max tier reached" vs "no points available")
// instead of a generic grey-out. The pure helper is exported separately so
// it can be unit-tested without rendering.

import { TIER_CAP } from './archetypes.js';
import type { DoctrineState } from './types.js';

export type DoctrineGateReason = 'at_cap' | 'no_points' | null;

/**
 * Return the reason an investment is blocked at `archetypeIdx`, or null if
 * nothing blocks it. `null` state means "no player loaded yet" — that's
 * treated as the same "no points" gate because the player has nothing to
 * spend regardless.
 *
 * Cap is checked before available: when both gates apply (defensive — the
 * server should prevent this), the more informative signal wins because
 * spending more of a capped archetype can never help.
 */
export function doctrineGateReason(
  state: DoctrineState | null,
  archetypeIdx: number,
): DoctrineGateReason {
  if (state === null) return 'no_points';
  const spent = state.spent;
  // Out-of-range indices are treated as at-cap so the UI hides the button
  // rather than letting an off-the-end click through to the sim.
  const points =
    archetypeIdx >= 0 && archetypeIdx < spent.length ? (spent[archetypeIdx] ?? 0) : TIER_CAP;
  if (points >= TIER_CAP) return 'at_cap';
  if (state.available <= 0) return 'no_points';
  return null;
}
